// Low-level XHR upload with progress AND cancel. `xhrUpload` in api/client.ts
// can't be cancelled — we need cancel for the tray's ✕ button and for retry
// (which aborts the running attempt before starting a new one).

import { ApiError } from "@/api/client";

export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

// Почему ats-api не кладёт запись в эту встречу (meetings._upload_target_error).
const MEETING_UPLOAD_409: Record<string, string> = {
  meeting_cancelled: "встречу отменили — запись к ней не приложить",
  meeting_no_show: "встреча отмечена «Не пришёл» — запись к ней не приложить",
  meeting_expired: "ссылка на встречу истекла — запись к ней не приложить",
  meeting_not_started: "встреча ещё не началась — запись к ней не приложить",
};

/** Строка ошибки в трее загрузок. Сервер отвечает кодами по-английски, а
 *  nginx на слишком большой файл — голым 413 без тела (HTTP/2 — даже без
 *  statusText); здесь — то, что рекрутёру понятно. `meeting` — запись шла к
 *  встрече («Встреча прошла?»): её 404/409 — про встречу, а не про файл. */
export function uploadErrorText(
  status: number,
  detail: string,
  o: { media?: boolean; meeting?: boolean } = {},
): string {
  if (status === 413) {
    return o.media ? "файл больше 2 ГБ — сервер такие не принимает" : "файл слишком большой для сервера";
  }
  if (status === 415) return "это не видео и не аудио";
  if (o.meeting && status === 404) return "встреча не найдена — обновите чат";
  if (o.meeting && status === 409) return MEETING_UPLOAD_409[detail] ?? "к этой встрече запись не приложить";
  return detail;
}

export function xhrUploadCancellable<T>(
  path: string,
  file: File,
  onProgress?: (pct: number) => void,
  extraFields?: Record<string, string>,
): UploadHandle<T> {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open("POST", "/api" + path);
    xhr.withCredentials = true;
    xhr.timeout = 0;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          resolve(undefined as T);
        }
      } else {
        let detail = xhr.statusText || `HTTP ${xhr.status}`;
        try {
          const b = JSON.parse(xhr.responseText);
          if (b?.detail && typeof b.detail === "string") detail = b.detail;
          else if (Array.isArray(b?.detail) && b.detail[0]?.msg) detail = String(b.detail[0].msg);
        } catch {
          /* non-JSON */
        }
        reject(new ApiError(xhr.status || 0, detail));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "соединение прервано при загрузке"));
    xhr.onabort = () => reject(new ApiError(0, "загрузка отменена"));
    const form = new FormData();
    form.append("file", file);
    if (extraFields) for (const [k, v] of Object.entries(extraFields)) form.append(k, v);
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}
